import { TranslocoService } from '@ngneat/transloco';
import {
  HdGateLineActivationOrigin,
  HdGateLineFixationCauseLabels,
  HdGateLineViewModel,
  buildHdGateLineViewModel,
} from '../chart/bodygraph/gate-info/line/hd-gate-line.view-model';
import { HdGateInfoService } from '../chart/bodygraph/hd-gate-info.service';
import { HdPlanetInfoService } from '../chart/bodygraph/hd-planet-info.service';
import { getHdPlanetDisplayConfigByPlanetId } from '../chart/bodygraph/planet-grid/hd-planet-display-config';

/**
 * `(poort, lijn, planetId, zijde?, oorsprong?)` → lijnkaart-VM, of `null` zonder
 * lijn (zuiver poort-segment) of zonder vertaling. Default: personality-kant als
 * transit-activatie (transits-kalender, Just Now); cycle-/composite-kaarten
 * geven de zijde en oorsprong (`chart` = eigen chart, zwart/rood) mee.
 */
export type HdTransitGateLineVmFactory = (
  gate: number,
  line: number | null,
  planetId: number,
  side?: 'personality' | 'design',
  origin?: HdGateLineActivationOrigin,
) => HdGateLineViewModel | null;

export interface HdTransitGateLineVmFactoryDeps {
  readonly lang: string;
  readonly gateInfo: HdGateInfoService;
  readonly planetInfo: HdPlanetInfoService;
  readonly transloco: TranslocoService;
}

/**
 * Bouwer van de I Tjing-lijnkaart (`hd-gate-line`) voor een planeet op
 * `poort.lijn`: de planeet staat als activatie op de kaart (chip in transit-
 * of chart-stijl; exaltatie/detriment kleuren mee wanneer zij de fixatieplaneet
 * is). Eén factory per taal, met een cache per `poort.lijn|planeet|zijde|
 * oorsprong`: het VM behoudt zijn identiteit tussen renders, zodat
 * `hd-gate-line` (OnPush) niet hertekent wanneer de omliggende lijst verschuift
 * (dag-flow-venster, tijdslider-stand, uitklappen van een ander item).
 *
 * Gedeeld door de dag-flow van de transits-kalender, het Just Now-paneel en de
 * uitklapkaarten in het Geavanceerd-paneel (`hd-cycle-cards`). Aanroepers maken
 * de factory opnieuw bij elke taal-/translation-load (bv. in een `computed` op
 * `lang`), zodat de cache nooit lege pre-load-teksten vasthoudt.
 */
export function createHdTransitGateLineVmFactory(deps: HdTransitGateLineVmFactoryDeps): HdTransitGateLineVmFactory {
  const { lang, gateInfo, planetInfo, transloco } = deps;
  const planetTranslations = planetInfo.planetTranslationsAllIn(lang);
  const iconLabels = gateInfo.iconLabelsIn(lang);
  const fixationCauseLabels: HdGateLineFixationCauseLabels = {
    prefix: transloco.translate('humanDesign.gateInfo.fixationCause.prefix', undefined, lang),
    inThisGate: (planetName: string) => planetName,
    inHarmonicGate: (planetName: string, harmonicGate: number) =>
      transloco.translate('humanDesign.gateInfo.fixationCause.inHarmonicGate', { planet: planetName, gate: harmonicGate }, lang),
    transitSource: (causeLabel: string) =>
      transloco.translate('humanDesign.gateInfo.fixationCause.transitSource', { cause: causeLabel }, lang),
  };
  const cache = new Map<string, HdGateLineViewModel | null>();
  return (gate, line, planetId, side = 'personality', origin = 'transit') => {
    if (line == null || line < 1 || line > 6) return null;
    const key = `${gate}.${line}|${planetId}|${side}|${origin}`;
    const cached = cache.get(key);
    if (cached !== undefined) return cached;
    const translations = gateInfo.gateInfoIn(gate, lang).translations;
    const lineTranslation = translations.lines[line - 1];
    const vm = lineTranslation == null
      ? null
      : buildHdGateLineViewModel({
          gate,
          lineNumber: line,
          line: lineTranslation,
          planetTranslations,
          iconLabels,
          voice: translations.voice,
          fearDetail: gateInfo.fearIconDetailIn(gate, lang),
          gateActivations: [{
            planetId,
            planet: getHdPlanetDisplayConfigByPlanetId(planetId)?.label ?? `${planetId}`,
            side,
            gate,
            line,
            fixing: 'none',
            origin,
          }],
          fixationCauseLabels,
        });
    cache.set(key, vm);
    return vm;
  };
}
